import {
  Avatar,
  Badge,
  Button,
  Code,
  CopyButton,
  Group,
  Modal,
  NumberInput,
  Select,
  Skeleton,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconArrowLeft, IconCopy, IconUserPlus, IconX } from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import type { MessageKey } from "../../shared/i18n";
import {
  endpoints,
  type OrganizationInvitation,
  type OrganizationMember,
  type OrganizationRole,
} from "../api/client";
import { meQuery, organizationQuery, organizationsQuery, useNotifyError } from "../api/queries";
import { useAuth } from "../auth/provider";
import { AppFrame } from "../components/app-frame";
import { ConfirmDialog } from "../components/confirm-dialog";
import { EmptyState } from "../components/empty-state";
import { Page, PageHeader } from "../components/page";
import { formatDate } from "../i18n/format";
import { useLocale, useMessages } from "../i18n/provider";
import styles from "./organization.module.css";

const ROLE_KEYS: Record<OrganizationRole, MessageKey> = {
  owner: "organization.role.owner",
  admin: "organization.role.admin",
  member: "organization.role.member",
};

export function OrganizationPage() {
  const { id = "" } = useParams();
  const t = useMessages();
  const { locale } = useLocale();
  const { me } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const notifyError = useNotifyError();
  const [params, setParams] = useSearchParams();

  const {
    data: organization,
    isLoading,
    isError,
  } = useQuery({
    ...organizationQuery(id),
    enabled: Boolean(id),
  });

  const [seats, setSeats] = useState<number | string>(1);
  const [renaming, setRenaming] = useState(false);
  const [name, setName] = useState("");
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<"admin" | "member">("member");
  const [createdInvite, setCreatedInvite] = useState<OrganizationInvitation | null>(null);
  const [removing, setRemoving] = useState<OrganizationMember | null>(null);
  const [leaving, setLeaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const minimumSeats = organization
    ? Math.max(organization.memberCount + organization.pendingInvites, 1)
    : 1;

  useEffect(() => {
    if (!organization) return;
    setSeats(Math.max(organization.subscription?.seats ?? 0, minimumSeats));
  }, [
    organization?.id,
    organization?.subscription?.seats,
    organization?.memberCount,
    organization?.pendingInvites,
  ]);

  const checkoutStatus = params.get("checkout");

  useEffect(() => {
    if (checkoutStatus !== "success" && checkoutStatus !== "cancelled") return;
    notifications.show({
      color: checkoutStatus === "success" ? "green" : "gray",
      message:
        checkoutStatus === "success"
          ? t("organization.checkoutSuccess")
          : t("organization.checkoutCancelled"),
      autoClose: 5000,
    });
    setParams({}, { replace: true });
    if (checkoutStatus === "success") {
      void queryClient.invalidateQueries({ queryKey: ["organization", id] });
      void queryClient.invalidateQueries({ queryKey: organizationsQuery.queryKey });
      void queryClient.invalidateQueries({ queryKey: meQuery.queryKey });
    }
  }, [checkoutStatus, setParams, queryClient, id, t]);

  const invalidate = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["organization", id] }),
      queryClient.invalidateQueries({ queryKey: organizationsQuery.queryKey }),
      queryClient.invalidateQueries({ queryKey: meQuery.queryKey }),
    ]);
  };

  const rename = useMutation({
    mutationFn: (next: string) => endpoints.updateOrganization(id, { name: next }),
    onSuccess: async () => {
      setRenaming(false);
      await invalidate();
      notifications.show({ color: "gray", message: t("organization.saved"), autoClose: 2000 });
    },
    onError: notifyError,
  });

  const invite = useMutation({
    mutationFn: () =>
      endpoints.inviteMember(id, {
        email: inviteEmail.trim(),
        role: inviteRole,
      }),
    onSuccess: async (invitation) => {
      setCreatedInvite(invitation);
      setInviteEmail("");
      await invalidate();
    },
    onError: notifyError,
  });

  const revoke = useMutation({
    mutationFn: (invitationId: string) => endpoints.revokeInvitation(id, invitationId),
    onSuccess: invalidate,
    onError: notifyError,
  });

  const changeRole = useMutation({
    mutationFn: (input: { memberId: string; role: OrganizationRole }) =>
      endpoints.updateMember(id, input.memberId, input.role),
    onSuccess: async () => {
      await invalidate();
      notifications.show({
        color: "gray",
        message: t("organization.roleChanged"),
        autoClose: 2000,
      });
    },
    onError: notifyError,
  });

  const remove = useMutation({
    mutationFn: (memberId: string) => endpoints.removeMember(id, memberId),
    onSuccess: async () => {
      await invalidate();
      notifications.show({
        color: "gray",
        message: t("organization.memberRemoved"),
        autoClose: 2000,
      });
    },
    onError: notifyError,
  });

  const leave = useMutation({
    mutationFn: () => endpoints.leaveOrganization(id),
    onSuccess: async () => {
      await invalidate();
      navigate("/organization");
    },
    onError: notifyError,
  });

  const removeOrganization = useMutation({
    mutationFn: () => endpoints.deleteOrganization(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: organizationsQuery.queryKey });
      await queryClient.invalidateQueries({ queryKey: meQuery.queryKey });
      navigate("/organization");
    },
    onError: notifyError,
  });

  const checkout = useMutation({
    mutationFn: (interval: "month" | "year") =>
      endpoints.organizationCheckout(id, {
        interval,
        seats: typeof seats === "number" ? seats : Number(seats) || minimumSeats,
      }),
    onSuccess: (result) => window.location.assign(result.url),
    onError: notifyError,
  });

  const portal = useMutation({
    mutationFn: () => endpoints.organizationPortal(id),
    onSuccess: (result) => window.location.assign(result.url),
    onError: notifyError,
  });

  if (isLoading) {
    return (
      <AppFrame active="/organization">
        <Page width="narrow">
          <Skeleton height={36} width="40%" radius="md" mb="lg" />
          <Skeleton height={180} radius="lg" mb="lg" />
          <Skeleton height={220} radius="lg" />
        </Page>
      </AppFrame>
    );
  }

  if (isError || !organization) {
    return (
      <AppFrame active="/organization">
        <Page width="narrow">
          <EmptyState
            title={t("errors.notFound")}
            action={
              <Button component={Link} to="/organization" size="sm">
                {t("organization.backToOrgs")}
              </Button>
            }
          />
        </Page>
      </AppFrame>
    );
  }

  const isOwner = organization.role === "owner";
  const canManage = isOwner || organization.role === "admin";
  const active = organization.plan === "pro";
  const usedSeats = organization.memberCount + organization.pendingInvites;
  const seatsFull = usedSeats >= organization.seats;
  const ownerCount = organization.members.filter((member) => member.role === "owner").length;
  const lastOwner = isOwner && ownerCount <= 1;

  const closeInvite = () => {
    setInviteOpen(false);
    setCreatedInvite(null);
    setInviteEmail("");
    setInviteRole("member");
  };

  return (
    <AppFrame active="/organization">
      <Page width="narrow">
        <Button
          component={Link}
          to="/organization"
          variant="subtle"
          color="gray"
          size="compact-sm"
          leftSection={<IconArrowLeft size={16} stroke={1.5} />}
          mb="md"
        >
          {t("organization.backToOrgs")}
        </Button>

        <PageHeader
          title={
            <Group gap={10} align="center">
              {organization.name}
              <Badge variant="light" color="gray" size="sm">
                {t(ROLE_KEYS[organization.role])}
              </Badge>
            </Group>
          }
          description={
            <Text size="sm" c="dimmed">
              {t("organization.members", { count: organization.memberCount })}
            </Text>
          }
          action={
            canManage ? (
              <Button
                variant="default"
                onClick={() => {
                  setName(organization.name);
                  setRenaming(true);
                }}
              >
                {t("organization.rename")}
              </Button>
            ) : null
          }
        />

        <section className={styles.section}>
          <Group justify="space-between" align="flex-start">
            <div>
              <Text fw={600}>{t("organization.billingTitle")}</Text>
              <Text size="sm" c="dimmed" mt={4}>
                {t("organization.billingBody")}
              </Text>
            </div>
            <Badge variant="light" color={active ? "blue" : "gray"} size="sm">
              {active ? t("organization.planActive") : t("organization.planInactive")}
            </Badge>
          </Group>

          {organization.subscription ? (
            <Stack gap={4} mt="md">
              <Text size="sm">
                {t("organization.status")}: {organization.subscription.status}
              </Text>
              <Text size="sm" c="dimmed">
                {t("organization.seatUsage", {
                  used: usedSeats,
                  seats: organization.seats,
                })}
              </Text>
              {organization.subscription.currentPeriodEnd ? (
                <Text size="sm" c="dimmed">
                  {organization.subscription.cancelAtPeriodEnd
                    ? t("organization.ends", {
                        date: formatDate(organization.subscription.currentPeriodEnd, locale),
                      })
                    : t("organization.renews", {
                        date: formatDate(organization.subscription.currentPeriodEnd, locale),
                      })}
                </Text>
              ) : null}
            </Stack>
          ) : null}

          {canManage && active && organization.subscription?.portalAvailable ? (
            <Stack gap={6} mt="md">
              <Button
                variant="light"
                onClick={() => portal.mutate()}
                loading={portal.isPending}
                className={styles.selfStart}
              >
                {t("organization.manageBilling")}
              </Button>
              <Text size="xs" c="dimmed">
                {t("organization.manageSeats")}
              </Text>
            </Stack>
          ) : null}

          {canManage && !active ? (
            <div className={styles.checkout}>
              <NumberInput
                label={t("organization.seats")}
                description={t("organization.seatsHint")}
                min={minimumSeats}
                max={500}
                value={seats}
                onChange={setSeats}
                allowDecimal={false}
              />
              <Group gap="sm" align="center">
                <Button
                  onClick={() => checkout.mutate("month")}
                  loading={checkout.isPending && checkout.variables === "month"}
                  disabled={checkout.isPending}
                >
                  {t("organization.teamPrice")} {t("organization.perMonth")}
                </Button>
                <Button
                  variant="light"
                  onClick={() => checkout.mutate("year")}
                  loading={checkout.isPending && checkout.variables === "year"}
                  disabled={checkout.isPending}
                >
                  {t("organization.yearlyPrice")} {t("organization.perYear")}
                </Button>
              </Group>
            </div>
          ) : null}
        </section>

        <section className={styles.section}>
          <Group justify="space-between" align="flex-start">
            <div>
              <Text fw={600}>{t("organization.membersTitle")}</Text>
              <Text size="sm" c="dimmed" mt={4}>
                {active
                  ? t("organization.seatUsage", {
                      used: usedSeats,
                      seats: organization.seats,
                    })
                  : t("organization.subscriptionRequired")}
              </Text>
            </div>
            {canManage ? (
              <Button
                size="sm"
                leftSection={<IconUserPlus size={16} stroke={1.5} />}
                disabled={!active || seatsFull}
                onClick={() => setInviteOpen(true)}
              >
                {t("organization.invite")}
              </Button>
            ) : null}
          </Group>
          {canManage && active && seatsFull ? (
            <Text size="sm" c="dimmed" mt="sm">
              {t("organization.noSeats")}
            </Text>
          ) : null}

          <Stack gap={0} mt="md">
            {organization.members.map((member) => {
              const isYou = member.userId === me?.id;
              const canRemove = canManage && !isYou && (isOwner || member.role === "member");
              return (
                <div key={member.id} className={styles.memberRow}>
                  <Group gap={12} wrap="nowrap" className={styles.memberIdentity}>
                    <Avatar size={36} radius="xl" src={member.image ?? undefined}>
                      {(member.name || member.email).slice(0, 1).toUpperCase()}
                    </Avatar>
                    <div className={styles.memberText}>
                      <Text size="sm" fw={500} lineClamp={1}>
                        {member.name}
                        {isYou ? ` · ${t("organization.you")}` : ""}
                      </Text>
                      <Text size="xs" c="dimmed" lineClamp={1}>
                        {member.email}
                      </Text>
                    </div>
                  </Group>
                  <Group gap={8} wrap="nowrap">
                    {isOwner && !isYou ? (
                      <Select
                        size="xs"
                        w={118}
                        value={member.role}
                        allowDeselect={false}
                        data={[
                          { value: "owner", label: t("organization.role.owner") },
                          { value: "admin", label: t("organization.role.admin") },
                          { value: "member", label: t("organization.role.member") },
                        ]}
                        onChange={(role) => {
                          if (role) {
                            changeRole.mutate({
                              memberId: member.id,
                              role: role as OrganizationRole,
                            });
                          }
                        }}
                      />
                    ) : (
                      <Badge variant="light" color="gray" size="sm">
                        {t(ROLE_KEYS[member.role])}
                      </Badge>
                    )}
                    {canRemove ? (
                      <Button
                        variant="subtle"
                        color="red"
                        size="compact-xs"
                        onClick={() => setRemoving(member)}
                        aria-label={t("organization.removeMember")}
                      >
                        <IconX size={14} stroke={1.5} />
                      </Button>
                    ) : null}
                  </Group>
                </div>
              );
            })}
          </Stack>
        </section>

        {canManage ? (
          <section className={styles.section}>
            <Text fw={600}>{t("organization.invitationsTitle")}</Text>
            {organization.invitations.length === 0 ? (
              <Text size="sm" c="dimmed" mt="sm">
                {t("organization.noPendingInvites")}
              </Text>
            ) : (
              <Stack gap={0} mt="md">
                {organization.invitations.map((invitation) => (
                  <div key={invitation.id} className={styles.inviteRow}>
                    <div className={styles.memberText}>
                      <Text size="sm" fw={500} lineClamp={1}>
                        {invitation.email}
                      </Text>
                      <Text size="xs" c="dimmed">
                        {t(ROLE_KEYS[invitation.role])} ·{" "}
                        {t("organization.inviteExpires", {
                          date: formatDate(invitation.expiresAt, locale),
                        })}
                      </Text>
                    </div>
                    <Group gap={6} wrap="nowrap">
                      <CopyButton value={invitation.inviteUrl}>
                        {({ copied, copy }) => (
                          <Button
                            variant="subtle"
                            color="gray"
                            size="compact-xs"
                            leftSection={<IconCopy size={14} stroke={1.5} />}
                            onClick={copy}
                          >
                            {copied ? t("organization.copied") : t("organization.copyLink")}
                          </Button>
                        )}
                      </CopyButton>
                      <Button
                        variant="subtle"
                        color="red"
                        size="compact-xs"
                        onClick={() => revoke.mutate(invitation.id)}
                      >
                        {t("organization.revokeInvite")}
                      </Button>
                    </Group>
                  </div>
                ))}
              </Stack>
            )}
          </section>
        ) : null}

        <section className={styles.section}>
          <Text fw={600}>{t("organization.settingsTitle")}</Text>
          <Text size="sm" c="dimmed" mt={4}>
            {t("organization.roleHint")}
          </Text>
          <Group gap="sm" mt="md">
            <Button
              variant="default"
              color="red"
              disabled={lastOwner || leave.isPending}
              onClick={() => setLeaving(true)}
            >
              {t("organization.leave")}
            </Button>
            {isOwner ? (
              <Button color="red" variant="light" onClick={() => setDeleting(true)}>
                {t("organization.delete")}
              </Button>
            ) : null}
          </Group>
          {lastOwner ? (
            <Text size="xs" c="dimmed" mt={8}>
              {t("organization.ownerLeave")}
            </Text>
          ) : null}
        </section>
      </Page>

      <Modal
        opened={renaming}
        onClose={() => setRenaming(false)}
        title={t("organization.rename")}
        size="sm"
      >
        <Stack>
          <TextInput
            label={t("organization.name")}
            value={name}
            onChange={(event) => setName(event.currentTarget.value)}
            maxLength={80}
            data-autofocus
          />
          <Group justify="flex-end" mt="sm">
            <Button variant="subtle" color="gray" onClick={() => setRenaming(false)}>
              {t("common.cancel")}
            </Button>
            <Button
              disabled={name.trim().length < 2}
              loading={rename.isPending}
              onClick={() => rename.mutate(name.trim())}
            >
              {t("common.save")}
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={inviteOpen}
        onClose={closeInvite}
        title={t("organization.inviteTitle")}
        size="sm"
      >
        {createdInvite ? (
          <Stack gap="md">
            <Text size="sm" c="dimmed">
              {t("organization.inviteSent")}
            </Text>
            <div>
              <Text size="sm" fw={500} mb={6}>
                {t("organization.inviteLink")}
              </Text>
              <Code block className={styles.inviteLink}>
                {createdInvite.inviteUrl}
              </Code>
            </div>
            <Group justify="flex-end">
              <CopyButton value={createdInvite.inviteUrl}>
                {({ copied, copy }) => (
                  <Button
                    variant="light"
                    leftSection={<IconCopy size={16} stroke={1.5} />}
                    onClick={copy}
                  >
                    {copied ? t("organization.copied") : t("organization.copyLink")}
                  </Button>
                )}
              </CopyButton>
              <Button onClick={closeInvite}>{t("common.done")}</Button>
            </Group>
          </Stack>
        ) : (
          <Stack>
            <TextInput
              label={t("organization.inviteEmail")}
              placeholder={t("organization.emailPlaceholder")}
              value={inviteEmail}
              onChange={(event) => setInviteEmail(event.currentTarget.value)}
              type="email"
              data-autofocus
            />
            <Select
              label={t("organization.inviteRole")}
              value={inviteRole}
              allowDeselect={false}
              data={[
                { value: "member", label: t("organization.role.member") },
                { value: "admin", label: t("organization.role.admin") },
              ]}
              onChange={(role) => setInviteRole(role === "admin" ? "admin" : "member")}
            />
            <Group justify="flex-end" mt="sm">
              <Button variant="subtle" color="gray" onClick={closeInvite}>
                {t("common.cancel")}
              </Button>
              <Button
                disabled={!inviteEmail.includes("@")}
                loading={invite.isPending}
                onClick={() => invite.mutate()}
              >
                {t("organization.invite")}
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>

      <ConfirmDialog
        opened={Boolean(removing)}
        onClose={() => setRemoving(null)}
        title={t("organization.removeMember")}
        body={t("organization.removeConfirm")}
        confirmLabel={t("organization.removeMember")}
        onConfirm={async () => {
          if (!removing) return;
          try {
            await remove.mutateAsync(removing.id);
          } catch (error) {
            notifyError(error);
          }
        }}
      />

      <ConfirmDialog
        opened={leaving}
        onClose={() => setLeaving(false)}
        title={t("organization.leave")}
        body={t("organization.leaveConfirm")}
        confirmLabel={t("organization.leave")}
        loading={leave.isPending}
        onConfirm={async () => {
          try {
            await leave.mutateAsync();
          } catch (error) {
            notifyError(error);
          }
        }}
      />

      <ConfirmDialog
        opened={deleting}
        onClose={() => setDeleting(false)}
        title={t("organization.delete")}
        body={`${t("organization.deleteConfirm")} ${t("organization.deleteBody")}`}
        confirmLabel={t("organization.delete")}
        loading={removeOrganization.isPending}
        onConfirm={async () => {
          try {
            await removeOrganization.mutateAsync();
          } catch (error) {
            notifyError(error);
          }
        }}
      />
    </AppFrame>
  );
}
